import React, { useEffect, useState } from 'react'
import { toUnicode } from 'punycode'
import SVG from 'react-inlinesvg'
import { getRequest } from '../../../services'
import {
  Tooltip,
  useSortableTable,
  IInsightsV1StatisticsRow,
  TableHead,
  map,
  WithTooltip,
  navigateTo,
  varClass,
  formatStat,
  formatCurrency,
  IInsightsV1Statistics,
  Loadable,
  useApiPost,
  newTableSort,
} from '../../shared'
import {
  ignoreWorstNetworkUuids,
  pageviewsEnabled,
} from '../constants'
import moment from 'moment'
import { startOfDay, endOfDay } from 'date-fns'
import { CommissionDropTable } from './CommissionDropTable'
import { EcrDropTable } from './EcrDropTable'
import { NewCampaignsTable } from './NewCampaignsTable'
import { ClosedCampaignsTable } from './ClosedCampaignsTable'
import { PlaceholderTop } from '../Placeholder'

const performanceTabLabels = {
  'name': 'Merchants',
  'tracker': 'Campaigns',
  'network': 'Networks',
  'channel': 'Sites',
  'page': 'Pages',
}

const performanceViewKey = 'overview-performance-view'

const sortTop = newTableSort((a: any, b: any, key) => {
  if (key === 'cr') {
    return [
      a.clickCount > 0 ? a.conversionCount / a.clickCount : 0,
      b.clickCount > 0 ? b.conversionCount / b.clickCount : 0,
    ]
  }
  if (key === 'ecr') {
    return [
      a.conversionSale > 0 ? a.conversionTotalCommission / a.conversionSale : 0,
      b.conversionSale > 0 ? b.conversionTotalCommission / b.conversionSale : 0,
    ]
  }
  if (key === 'epc') {
    return [
      a.clickCount > 0 ? a.conversionTotalCommission / a.clickCount : 0,
      b.clickCount > 0 ? b.conversionTotalCommission / b.clickCount : 0,
    ]
  }
})

export const PerformanceTable = (props) => {
  const {
    data: totals,
    selectedChannelUuids,
    selectedCreatorNotIds,
    isEmpty,
    currentUser,
    channels,
    networks,
    advertisers,
  } = props

  const [view, setView] = useState(
    () => localStorage.getItem(performanceViewKey) || 'best'
  )
  const [tab, setTab] = useState(() => Object.keys(performanceTabLabels)[0])

  let fromDate = moment().toDate()
  const toDate = moment().subtract(1, 'days').toDate()
  let ignoredNetworks: string[] | undefined
  // let prevFromDate = moment().add(1, 'day').toDate()
  // let prevToDate = moment().add(1, 'day').toDate()
  if (view === 'best') {
    fromDate = moment().subtract(30, 'days').toDate()
  } else if (view === 'worst') {
    fromDate = moment().subtract(4, 'days').toDate()
    ignoredNetworks = ignoreWorstNetworkUuids
  }
  const data = useApiPost<IInsightsV1Statistics>(
    '/insights/v1/statistics',
    {
      fromDate: startOfDay(fromDate),
      toDate: endOfDay(toDate),
      groupBy: tab,
      filters: {
        channelUuids: selectedChannelUuids,
        creatorNotIds: selectedCreatorNotIds,
        networkNotUuids: ignoredNetworks,
        withNetworks: false,
      },
    },
    [
      tab,
      moment(fromDate).format('YYYY-MM-DD'),
      moment(toDate).format('YYYY-MM-DD'),
      JSON.stringify(selectedChannelUuids),
      JSON.stringify(selectedCreatorNotIds),
      view,
    ]
  )

  useEffect(() => {
    localStorage.setItem(performanceViewKey, view)
  }, [view])

  const currencyCode = currentUser.currency?.currencyCode || 'DKK'

  const noData =
    isEmpty &&
    totals &&
    totals.totals &&
    totals.totals.clicks?.count === 0 &&
    totals.totals.commission?.value === 0 &&
    totals.totals.conversions?.count === 0 &&
    totals.totals.pageviews?.count === 0

  const Tab = ({
    children,
    value,
  }: {
    children: React.ReactNode
    value: string
  }) => {
    return (
      <li className={varClass({ 'active': view === value })}>
        <button className="link" onClick={() => setView(value)}>
          {children}
        </button>
      </li>
    )
  }

  return (
    <div className="card overview-performance">
      <div className="page-subnav m-b-4">
        <div className="nav">
          <ul className="m-r-160">
            <Tab value="best">Best performing (30 days)</Tab>
            <Tab value="worst">Worst performing (4 days)</Tab>
            <Tab value="commission-drop">Drop in commission (14 days)</Tab>
            <Tab value="ecr-drop">Drop in ECR (3 days / 30 days)</Tab>
            <Tab value="new-campaigns">New campaigns (30 days)</Tab>
            <Tab value="closed-campaigns">
              Recently closed campaigns (14 days)
            </Tab>
          </ul>
        </div>
      </div>
      {view === 'best' || view === 'worst' ? (
        <>
          <div className="page-subnav">
            <div className="nav p-b-2 m-t--4 m-b-0">
              <ul>
                {map(performanceTabLabels, (key, label) => (
                  <li
                    key={key}
                    className={varClass({
                      'active': tab === key,
                    })}
                  >
                    <button
                      className="link p-y-4"
                      onClick={() => {
                        setTab(key)
                      }}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {view === 'worst' ? (
            <button
              className="btn btn-bordered btn-report vertical-middle m-t--3"
              onClick={(event) => {
                if (view === 'worst') {
                  navigateTo(event, `/insights/reports?tab=${tab}&crFilter=1`)
                }
              }}
            >
              Show report
              <SVG src="/images/chevron-right.svg" className="m-l-2" />
            </button>
          ) : null}
          <Loadable data={data} placeholder={<PlaceholderTop />}>
            <TopTable
              data={data?.rows || []}
              totals={totals?.totals || {}}
              view={view}
              tab={tab}
              currencyCode={currencyCode}
            />
            {noData && (
              <div className="awaiting">
                <SVG src="/images/overview2/awaiting.svg" />
              </div>
            )}
          </Loadable>
        </>
      ) : view === 'commission-drop' ? (
        <CommissionDropTable />
      ) : view === 'ecr-drop' ? (
        <EcrDropTable />
      ) : view === 'new-campaigns' ? (
        <NewCampaignsTable
          {...{
            channels,
            networks,
            advertisers,
          }}
        />
      ) : view === 'closed-campaigns' ? (
        <>
          <button
            className="btn btn-bordered btn-report vertical-middle m-t--3"
            onClick={(event) => {
              navigateTo(event, '/insights/closed')
            }}
          >
            Show more
            <SVG src="/images/chevron-right.svg" className="m-l-2" />
          </button>
          <ClosedCampaignsTable
            {...{
              channels,
              networks,
              advertisers,
            }}
          />
        </>
      ) : null}
    </div>
  )
}

const TopTable = (props) => {
  const { data, prevData, totals, view, tab, currencyCode } = props

  const { sort, dir, toggleSort } = useSortableTable(
    view === 'best'
      ? {
          sort: 'conversionTotalCommission',
          dir: 'desc',
        }
      : view === 'worst'
      ? {
          sort: 'cr',
          dir: 'asc',
        }
      : {
          sort: 'conversionTotalCommission',
          dir: 'desc',
        }
  )

  const findPrevItem = (row) => {
    if (!prevData?.length) return undefined
    return prevData.find(
      (prevRow) =>
        (row.name && prevRow.name === row.name) ||
        (row.grp && prevRow.grp === row.grp)
    )
  }

  const headProps = { sort, dir, toggleSort }
  const shownRows = sortTop(data, sort, dir)
    .filter((item: IInsightsV1StatisticsRow) => {
      if (view === 'worst') {
        const cr = (item.conversionCount / (item.clickCount || 1)) * 100
        return item.clickCount >= 500 && cr <= 0.8
      } else {
        return true
      }
    })
    .slice(0, view === 'best' ? 25 : 150) as IInsightsV1StatisticsRow[]

  const showPageviews =
    pageviewsEnabled && (tab === 'page' || tab === 'channel')

  useEffect(() => {
    if (!showPageviews && sort === 'pageviewCount') {
      toggleSort('conversionTotalCommission', 'desc')()
    }
  }, [showPageviews])

  return (
    <div className="table-container-overflow">
      <table className="table table-bordered table-sortable table-hoverable">
        <thead>
          <tr>
            <th className="width-xl">{performanceTabLabels[tab]}</th>
            {showPageviews && (
              <TableHead
                value="pageviewCount"
                label="Pageviews"
                className="width-sm"
                {...headProps}
                desc
              />
            )}
            <TableHead
              value="clickCount"
              label="Clicks"
              className="width-sm"
              {...headProps}
              desc
            />
            <TableHead
              value="conversionCount"
              label="Conversions"
              className="width-sm"
              {...headProps}
              desc
            />
            <TableHead
              value="conversionTotalCommission"
              label="Revenue"
              className="width-md"
              {...headProps}
              desc
            />
          </tr>
        </thead>
        <tbody>
          {map(shownRows, (item, index) => (
            <TopItem
              key={item[`${tab}Uuid`] || index}
              item={item}
              prevItem={findPrevItem(item)}
              sort={sort}
              totals={totals}
              view={view}
              tab={tab}
              showPageviews={showPageviews}
              currencyCode={currencyCode}
            />
          ))}
          {shownRows?.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-light text-bolder">
                No {performanceTabLabels[tab].toLowerCase()}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <Tooltip
        id="overview2-performance-tooltip"
        type="dark"
        effect="solid"
        place="bottom"
        rebuild={[shownRows]}
      />
    </div>
  )
}

const TopItem = (props) => {
  const {
    item,
    prevItem,
    sort,
    totals,
    view,
    tab,
    showPageviews,
    currencyCode,
  } = props

  return (
    <tr>
      <td>
        <div className="row row-center row-space-between">
          <WithTooltip
            className="top-value-name name text-dark text-bold"
            label={toUnicode(item.name || item.grp || 'Unknown')}
            tooltipId="overview2-performance-tooltip"
          />
          <div className="row row-center row-narrow row-nowrap">
            {tab === 'name' && (
              <button
                className="link link-new-lighter link-filter"
                onClick={async (event) => {
                  const response = await getRequest(
                    `/advs/v1/uuid/${item.name || item.grp}`
                  )
                  if (response.data) {
                    navigateTo(event, `/merchants/${response.data}`)
                  }
                }}
              >
                <SVG
                  src="/images/icon-details.svg"
                  className="vertical-middle"
                />
              </button>
            )}
          </div>
        </div>
      </td>
      {showPageviews && (
        <td
          className={varClass({
            'sort-hightlight': sort === 'pageviewCount',
          })}
        >
          <TopValue value={formatStat(item.pageviewCount)} color="red" />
          <TopBar
            show={sort === 'pageviewCount'}
            value={item.pageviewsCount}
            max={totals.pageviews.count}
            color="red"
          />
        </td>
      )}
      <td
        className={varClass({
          'sort-hightlight': sort === 'clickCount',
        })}
      >
        <TopValue value={formatStat(item.clickCount)} color="orange" />
        <TopBar
          show={sort === 'clickCount'}
          value={item.clickCount}
          max={totals.clicks?.count}
          color="orange"
        />
      </td>
      <td
        className={varClass({
          'sort-hightlight': sort === 'conversionCount',
        })}
      >
        <TopValue value={formatStat(item.conversionCount)} color="blue" />
        <TopBar
          show={sort === 'conversionCount'}
          value={item.conversionCount}
          max={totals.conversions?.count}
          color="blue"
        />
      </td>
      <td
        className={varClass({
          'sort-hightlight': sort === 'conversionTotalCommission',
        })}
      >
        <div className="text-right text-nowrap">
          <TopValue
            value={formatCurrency(
              item.conversionTotalCommission,
              item.currencyCode || currencyCode
            )}
            color="green"
          />
        </div>
        <TopBar
          show={sort === 'conversionTotalCommission'}
          value={item.conversionTotalCommission}
          max={totals.commission?.value}
          color="green"
        />
      </td>
    </tr>
  )
}

const TopValue = (props) => {
  const { value, color } = props

  return (
    <div className={`top-value top-value-${color} text-nowrap`}>{value}</div>
  )
}

const TopBar = (props) => {
  const { show, value, max, color } = props

  if (!show) {
    return null
  }

  return (
    <div className={`top-bar top-bar-${color}`}>
      <div style={{ width: `${(value / max) * 100}%` }} />
    </div>
  )
}
